import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Row, Col, Form, Button, Container, Card } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import Select from "react-select";

const IssueInventoryItem = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const editData = location.state?.issueData;
  const [items, setItems] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [form, setForm] = useState({
    transaction_id: editData?.transaction_id || "",
    item_id: editData?.item_id || "",
    available_qty: editData?.available_qty || 0,
    issue_to: editData?.issue_to || "Student",
    recipient_id: editData?.recipient_id || "",
    recipient_name: editData?.recipient_name || "",
    quantity: editData?.quantity || "",
    issue_date: editData?.issue_date || new Date().toISOString().split("T")[0],
    expected_return_date: editData?.expected_return_date || "",
    remarks: editData?.remarks || "",
    status: editData?.status || "Issued",
  });

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const typingTimer = useRef();

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await axios.post(baseUrl + "/inventoryitem/", {
          action: "READ",
          school_id: userObj.school_id
        });
        const fetchedItems = response.data || [];
        setItems(fetchedItems);

        if (editData && !form.available_qty) {
          const selectedItem = fetchedItems.find(it => it.item_id === editData.item_id);
          if (selectedItem) {
            setForm(prev => ({ ...prev, available_qty: selectedItem.quantity + (editData.quantity || 0) }));
          }
        }
      } catch (error) {
        console.error("Error fetching items:", error);
      }
    };
    fetchItems();
  }, [baseUrl, editData]);

  useEffect(() => {
    const fetchRecipients = async () => {
      try {
        let response;
        if (form.issue_to === "Teacher" || form.issue_to === "Staff") {
          response = await axios.post(baseUrl + "/employees/", {
            action: "READ",
            school_id: userObj.school_id,
          });
          setRecipients(response?.data || []);
        } else if (form.issue_to === "Student") {
          // For students, we clear it initially and fetch on typing
          setRecipients([]);
        } else {
          setRecipients([]);
        }
      } catch (error) {
        console.error("Error fetching recipients:", error);
      }
    };
    fetchRecipients();
  }, [form.issue_to, baseUrl, userObj.school_id, userObj.academic_year_id]);

  const fetchStudentsByQuery = async (q, limit = 50) => {
    try {
      const res = await axios.post(`${baseUrl}/studentdropdown`, {
        p_query: q,
        p_school_id: userObj.school_id,
        p_academic_year_id: userObj.academic_year_id,
        p_limit: limit
      });
      const rows = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setRecipients(rows);
      return rows;
    } catch (e) {
      console.error("studentdropdown error:", e);
      setRecipients([]);
      return [];
    }
  };

  const handleStudentTyping = (inputValue, { action }) => {
    if (action !== "input-change") return inputValue;
    clearTimeout(typingTimer.current);
    const q = (inputValue || "").trim();

    typingTimer.current = setTimeout(() => {
      if (q.length >= 3) {
        fetchStudentsByQuery(q, 50);
      } else {
        setRecipients([]);
      }
    }, 300);

    return inputValue;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "item_id") {
      const selectedItem = items.find((it) => it.item_id === parseInt(value));
      setForm((prev) => ({
        ...prev,
        item_id: value,
        available_qty: selectedItem ? selectedItem.quantity : 0,
      }));
    } else if (name === "issue_to") {
      setRecipients([]); // Clear current list to prevent showing wrong data
      setForm((prev) => ({
        ...prev,
        issue_to: value,
        recipient_id: "",
        recipient_name: "",
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleRecipientSelect = (opt) => {
    const value = opt ? opt.value : "";
    const raw = opt ? opt.raw : null;

    let nameStr = "";
    if (raw) {
      nameStr = raw.emp_name
        ? raw.emp_name
        : `${raw.student_first_name} ${raw.student_last_name || ""}`;
    }

    setForm((prev) => ({
      ...prev,
      recipient_id: value,
      recipient_name: nameStr.trim(),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.quantity > form.available_qty) {
      toast.error("Issue quantity cannot exceed available stock!");
      return;
    }

    if (form.expected_return_date && form.expected_return_date < form.issue_date) {
      toast.warning("Expected return date cannot be earlier than the issue date.");
      return;
    }

    try {
      const payload = {
        ...form,
        transaction_id: form.transaction_id || 0,
        item_id: form.item_id || 0,
        recipient_id: form.recipient_id || 0,
        expected_return_date: form.expected_return_date || null,
        school_id: userObj.school_id,
        createdby: userObj.user_id || userObj.userid,
        lastmodifiedby: userObj.user_id || userObj.userid,
        updated_by: userObj.user_id || userObj.userid,
        action: editData ? "UPDATE" : "CREATE",
        type: "ISSUE",
      };

      const response = await axios.post(baseUrl + "/inventorytransaction/", payload);

      if (response.status === 200 || response.status === 201) {
        toast.success(editData ? "Record Updated Successfully" : "Item Issued Successfully");
        setTimeout(() => navigate("/issuelist"), 1500);
      }
    } catch (error) {
      console.error("Error processing transaction:", error);
      toast.error("Failed to process transaction");
    }
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '38px',
      borderRadius: '4px',
      borderColor: '#dee2e6',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#dee2e6'
      }
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>Issue Item Details
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group className="mb-3">
                      <Form.Label>Item <span className="requiredStar">*</span></Form.Label>
                      <Form.Select
                        name="item_id"
                        value={form.item_id}
                        onChange={handleInputChange}
                        required
                        disabled={!!editData}
                      >
                        <option value="">Select Item</option>
                        {items.filter(it => it.is_active === 'A').map((it) => (
                          <option key={it.item_id} value={it.item_id}>
                            {it.item_name}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback>Required</Form.Control.Feedback>
                    </Form.Group>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group className="mb-3">
                      <Form.Label>Available Quantity</Form.Label>
                      <Form.Control
                        type="number"
                        value={form.available_qty}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group className="mb-3">
                      <Form.Label>Issue To <span className="requiredStar">*</span></Form.Label>
                      <Form.Select
                        name="issue_to"
                        value={form.issue_to}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="Teacher">Teacher</option>
                        <option value="Student">Student</option>
                        <option value="Staff">Staff</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group className="mb-3">
                      <Form.Label>Recipient Name <span className="requiredStar">*</span></Form.Label>
                      <Select
                        id="recipient_id"
                        classNamePrefix="react-select"
                        isClearable
                        isSearchable
                        required
                        placeholder={form.issue_to === "Student" ? "Type to search student..." : "Select Recipient"}
                        options={recipients.map((r) => ({
                          value: r.emp_id || r.student_id,
                          label: r.emp_name
                            ? `${r.emp_name}${r.emp_code ? ` (${r.emp_code})` : ""}`
                            : `${r.student_first_name || ""} ${r.student_last_name || ""}${r.admission_number ? ` (${r.admission_number})` : ""}`,
                          raw: r
                        }))}
                        value={form.recipient_id ? {
                          value: form.recipient_id,
                          label: form.recipient_name
                        } : null}
                        onInputChange={form.issue_to === "Student" ? handleStudentTyping : undefined}
                        onChange={handleRecipientSelect}
                        noOptionsMessage={({ inputValue }) =>
                          form.issue_to === "Student" && (inputValue?.trim()?.length ?? 0) < 3
                            ? "Type at least 3 characters…"
                            : "No matches"
                        }
                        filterOption={form.issue_to === "Student" ? null : undefined}
                        menuPortalTarget={document.body}
                        styles={customSelectStyles}
                      />
                    </Form.Group>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group className="mb-3">
                      <Form.Label>Quantity <span className="requiredStar">*</span></Form.Label>
                      <Form.Control
                        type="number"
                        name="quantity"
                        value={form.quantity}
                        onChange={(e) => {
                          let val = e.target.value;
                          if (val !== "") {
                            val = Math.max(1, parseInt(val) || 1);
                          }
                          handleInputChange({ target: { name: "quantity", value: val } });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === '.') {
                            e.preventDefault();
                          }
                        }}
                        required
                        min="1"
                        placeholder="Enter quantity"
                      />
                    </Form.Group>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group className="mb-3">
                      <Form.Label>Issue Date <span className="requiredStar">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        name="issue_date"
                        value={form.issue_date}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group className="mb-3">
                      <Form.Label>Expected Return</Form.Label>
                      <Form.Control
                        type="date"
                        name="expected_return_date"
                        value={form.expected_return_date}
                        onChange={handleInputChange}
                        min={form.issue_date}
                      />
                    </Form.Group>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group className="mb-3">
                      <Form.Label>Remarks</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={1}
                        name="remarks"
                        value={form.remarks}
                        onChange={handleInputChange}
                        placeholder="Remarks"
                      />
                    </Form.Group>
                  </div>
                </Col>
              </Row>
              <div className="d-flex justify-content-between mt-3">
                <div>
                  <Button
                    type="button"
                    className="btn btn-info clearBtn"
                    onClick={() => setForm({
                      item_id: "",
                      available_qty: 0,
                      issue_to: "Student",
                      recipient_name: "",
                      quantity: "",
                      issue_date: new Date().toISOString().split("T")[0],
                      expected_return_date: "",
                      remarks: "",
                      status: "Issued",
                    })}
                  >
                    Reset
                  </Button>
                </div>
                <div>
                  <Button
                    type="button"
                    className="btn btn-danger secondaryBtn me-2"
                    onClick={() => navigate("/issuelist")}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="btn btn-success primaryBtn"
                  >
                    {editData ? "Update Record" : "Submit"}
                  </Button>
                </div>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default IssueInventoryItem;
